import { DeliveryType, Order, OrderStatus } from '@api/Orders/Order';

/**
 * Orden y filtros del tablero de cocina (`/orders`), como funciones PURAS.
 *
 * Antes vivían dentro del callback del `onSnapshot` de `ListOrders`, y los
 * filtros de pantalla (buscador, "ocultar los de más tarde") estaban en las
 * deps del efecto: cada letra tecleada recreaba los dos listeners y volvía a
 * pagar la ventana completa del día (cada lectura de Firestore se cobra). Ahora
 * el listener solo entrega las órdenes ordenadas y lo visible se deriva con
 * estas funciones en un `useMemo`.
 *
 * Las reglas son las de siempre; solo cambiaron de lugar.
 */

/** Prioridad de columna: lo que necesita acción primero. */
const STATUS_ORDER: Record<string, number> = {
    [OrderStatus.Paid]: 1,
    [OrderStatus.WaitingForPayment]: 2,
    [OrderStatus.Preparing]: 3,
    [OrderStatus.Ready]: 4,
    [OrderStatus.Route]: 5,
    [OrderStatus.Completed]: 6,
    [OrderStatus.Cancelled]: 7
};

/** Por estatus y, dentro del estatus, la más vieja primero. No muta la entrada. */
export const sortBoardOrders = (orders: Order[]): Order[] =>
    [...orders].sort(
        (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.date.toMillis() - b.date.toMillis()
    );

/**
 * Regla de "ocultar los de más tarde" (`hideTodayLongTime`): un pickup pagado
 * para dentro de un rato no debe distraer a cocina hasta que llegue su hora.
 * Todo lo que no es delivery/pickup se ve siempre.
 */
export const isBoardOrderVisibleNow = (order: Order, now: Date): boolean => {
    if (!order.deliveryType) {
        return true;
    }
    if (order.reservationDate && order.reservationDate.toDate().toDateString() !== now.toDateString()) {
        return false;
    }
    if (order.status === OrderStatus.WaitingForPayment || order.status === OrderStatus.Cancelled) {
        return false;
    }
    const orderDate = order.date.toDate();
    const isToday = orderDate.toDateString() === now.toDateString();
    // Si no es de hoy, siempre mostrar (historial)
    if (!isToday) {
        return true;
    }
    // Pickup pagado: aparece cuando llega su hora
    if (order.deliveryType === DeliveryType.Pickup && order.status === OrderStatus.Paid) {
        return orderDate <= now;
    }
    // Delivery u otros casos: mostrar siempre
    return true;
};

/** Buscador del tablero: por nombre del cliente o del canal de venta. */
export const matchesBoardSearch = (order: Order, search: string): boolean => {
    const term = search.toLowerCase();
    return (
        (order.customer?.profile?.name || '').toLowerCase().includes(term) ||
        (order.salesChannel?.name || '').toLowerCase().includes(term)
    );
};

export interface BoardOrdersFilter {
    hideTodayLongTime: boolean;
    search: string;
    now: Date;
}

/** Lo que el tablero pinta, a partir de las órdenes ya ordenadas. */
export const filterBoardOrders = (orders: Order[], { hideTodayLongTime, search, now }: BoardOrdersFilter): Order[] => {
    const visible = hideTodayLongTime ? orders.filter((order) => isBoardOrderVisibleNow(order, now)) : orders;
    if (search !== '') {
        return visible.filter((order) => matchesBoardSearch(order, search));
    }
    return visible;
};

/** Dispara el sonido de "pedido nuevo": hay un pickup/delivery pagado a la vista. */
export const hasPaidDeliveryOrders = (orders: Order[]): boolean =>
    orders.some((order) => order.status === OrderStatus.Paid && !!order.deliveryType);
